import React from "react";

// Gedeelde helpers voor de subsidie-partnerflow (EIA / EIA Zon / ISDE via
// partner-link). Spiegel van de BTW-partnerflow; zie AGENTS.md sectie 3.28.

export const SUBSIDIE_MODULES = ["eia", "eiaZon", "isde"];

export const MODULE_LABELS = {
  btwBatterij: "BTW Batterij",
  eia: "EIA",
  eiaZon: "EIA Zon",
  isde: "ISDE",
};

export const REGLING_TO_MODULE = {
  EIA: "eia",
  "EIA Zon": "eiaZon",
  ISDE: "isde",
};

export const moduleLabel = (module) => MODULE_LABELS[module] ?? module ?? "";

// Label van een factuurregel: server stuurt `moduleLabel` mee (contract 7).
// Oude regels zonder dat veld zijn per definitie BTW.
export const lineModuleLabel = (line) =>
  line?.moduleLabel ??
  (line?.module ? moduleLabel(line.module) : null) ??
  (line?.applicationModel === "Applications" ? "Subsidie" : "BTW Batterij");

export const partnerModulesOf = (user) =>
  Array.isArray(user?.partnerModules) ? user.partnerModules : [];

export const isSubsidiePartner = (user) =>
  partnerModulesOf(user).some((m) => SUBSIDIE_MODULES.includes(m));

export const hasAnyPartnerRole = (user) =>
  !!user?.btwPartnerEnabled || partnerModulesOf(user).length > 0;

// Kleuren volgen de BTW-lijst: violet = te factureren, sky = gefactureerd,
// emerald = betaald, rose = gecrediteerd. Slate = nog niet goedgekeurd, dus
// nog geen factuurregel.
export const PARTNER_INVOICE_LABELS = {
  none: { label: "Partner", color: "bg-slate-100 text-slate-700" },
  pending: { label: "Te factureren", color: "bg-violet-100 text-violet-800" },
  invoiced: { label: "Factuur verstuurd", color: "bg-sky-100 text-sky-800" },
  paid: { label: "Betaald", color: "bg-emerald-100 text-emerald-800" },
  credited: { label: "Gecrediteerd", color: "bg-rose-100 text-rose-800" },
};

export const partnerNameOf = (row) =>
  row?.partnerUserId?.bedrijfsnaam ?? row?.partnerUserId?.naam ?? "";

export const PartnerBadge = ({ row, className = "" }) => {
  if (!row?.partnerUserId) return null;
  const status = row?.partnerInvoice?.status ?? "none";
  const cfg = PARTNER_INVOICE_LABELS[status] ?? PARTNER_INVOICE_LABELS.none;
  const naam = partnerNameOf(row);
  return (
    <span
      title={naam ? `Partner: ${naam}` : "Partner-aanvraag"}
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${cfg.color} ${className}`}
    >
      {status === "none" ? "Partner" : `Partner · ${cfg.label}`}
    </span>
  );
};

// Kandidaat voor de knop "Maak partner-factuur": alle geselecteerde rijen
// moeten dezelfde partner delen; alleen goedgekeurde aanvragen die nog niet
// op een factuur staan (of gecrediteerd zijn) mogen mee.
export const partnerInvoiceCandidateFromRows = (rows) => {
  if (!rows || rows.length === 0) return null;
  const partnerIds = new Set(
    rows
      .map((r) => r.partnerUserId?._id ?? r.partnerUserId)
      .filter(Boolean)
      .map(String),
  );
  if (partnerIds.size !== 1) return null;
  const withPartner = rows.filter((r) => r.partnerUserId);
  if (withPartner.length !== rows.length) return null;
  const partnerUserId = Array.from(partnerIds)[0];
  const isEligible = (r) => {
    if (r.status !== "Goedgekeurd") return false;
    const s = r.partnerInvoice?.status;
    return !s || s === "pending" || s === "credited";
  };
  const eligibleIds = rows.filter(isEligible).map((r) => r._id);
  if (eligibleIds.length === 0) return null;
  return {
    partnerUserId,
    partnerNaam: partnerNameOf(rows[0]),
    totalSelected: rows.length,
    eligibleIds,
    blockedCount: rows.length - eligibleIds.length,
  };
};
